/**
 * Error Details Panel
 * Sliding panel that displays detailed information about an error
 */

import './ScenarioDetailsPanel.css';
import { apiFetch as baseApiFetch } from '../utils/apiClient.js';
import { escapeHtml } from '../utils/html.js';

/**
 * Create error details panel
 * @param {Object} options
 * @param {Object} options.error - Error to display
 * @param {Function} options.onClose - Callback when panel is closed
 * @param {Function} options.authFetch - Function to make authenticated API calls
 * @param {Function} options.onErrorUpdated - Callback when error is updated (for admin to reload list)
 * @returns {HTMLElement}
 */
export function createErrorDetailsPanel(options = {}) {
  const {
    error,
    onClose,
    authFetch,
    onErrorUpdated,
  } = options;

  const apiFetch = (path, options = {}) => {
    if (typeof authFetch === 'function') {
      return authFetch(path, options);
    }
    return baseApiFetch(path, options);
  };

  const container = document.createElement('div');

  // Overlay (backdrop)
  const overlay = document.createElement('div');
  overlay.className = 'details-panel-overlay';
  overlay.addEventListener('click', handleClose);
  container.appendChild(overlay);

  // Panel
  const panel = document.createElement('div');
  panel.className = 'details-panel';

  // Header
  const header = createHeader();
  panel.appendChild(header);

  // Content (scrollable)
  const content = document.createElement('div');
  content.className = 'details-panel-content';

  // General information section
  const generalSection = createGeneralInfoSection();
  content.appendChild(generalSection);

  // Message section
  const messageSection = createMessageSection();
  content.appendChild(messageSection);

  // Stack trace section
  if (error?.stack) {
    const stackSection = createStackSection();
    content.appendChild(stackSection);
  }

  // User section
  if (error?.userId || error?.userEmail) {
    const userSection = createUserSection();
    content.appendChild(userSection);
  }

  // Request context section
  if (error?.url || error?.path || error?.method) {
    const requestSection = createRequestSection();
    content.appendChild(requestSection);
  }

  // Code location section
  if (error?.file || error?.line) {
    const codeSection = createCodeSection();
    content.appendChild(codeSection);
  }

  // Environment section
  if (error?.browserInfo || error?.electronVersion || error?.environment || error?.apiUrl) {
    const envSection = createEnvironmentSection();
    content.appendChild(envSection);
  }

  // Metadata section
  if (error?.metadata) {
    const metadataSection = createMetadataSection();
    content.appendChild(metadataSection);
  }

  // Resolution section
  const resolutionSection = createResolutionSection();
  content.appendChild(resolutionSection);

  panel.appendChild(content);

  // Footer (with resolve action if not resolved)
  if (!error?.resolved) {
    const footer = createFooter();
    panel.appendChild(footer);
  }

  container.appendChild(panel);

  // Activate panel after a brief delay for animation
  setTimeout(() => {
    overlay.classList.add('active');
    panel.classList.add('active');
  }, 10);

  // Fonction helper pour formater les valeurs
  function formatValue(value, defaultValue = '—') {
    if (value === null || value === undefined) return defaultValue;
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  }

  function formatDateTime(date) {
    if (!date) return '—';
    try {
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return '—';
    }
  }

  function createHeader() {
    const header = document.createElement('header');
    header.className = 'details-panel-header';

    const top = document.createElement('div');
    top.className = 'details-panel-header-top';
    top.style.cssText = 'display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem;';

    const titleWrapper = document.createElement('div');
    titleWrapper.className = 'details-panel-title-wrapper';
    titleWrapper.innerHTML = `
      <span class="details-panel-icon material-symbols-outlined">error</span>
      <div class="details-panel-title-group">
        <h2 class="details-panel-title">Détails de l'erreur</h2>
        <p class="details-panel-subtitle">Informations complètes sur l'erreur système</p>
      </div>
    `;
    top.appendChild(titleWrapper);

    // Right section with level badge and close button
    const rightSection = document.createElement('div');
    rightSection.style.cssText = 'display: flex; align-items: center; gap: 1rem; flex-shrink: 0;';

    // Level badge
    const level = error?.level || 'error';
    const levelConfig = {
      error: { text: 'Erreur', color: '#c9453a', bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.3)', icon: 'error' },
      warn: { text: 'Avertissement', color: '#b7791f', bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)', icon: 'warning' },
      info: { text: 'Info', color: '#29768e', bg: 'rgba(56, 151, 152, 0.1)', border: 'rgba(56, 151, 152, 0.3)', icon: 'info' },
    };
    const config = levelConfig[level] || levelConfig.error;

    const levelBadge = document.createElement('div');
    levelBadge.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';
    levelBadge.innerHTML = `
      <span class="ad-pill" style="--ad-tone-fg: ${config.color}; --ad-tone-bg: ${config.bg};">${config.text}</span>
    `;
    rightSection.appendChild(levelBadge);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'details-panel-close-btn';
    closeBtn.innerHTML = '<span class="material-symbols-outlined">close</span>';
    closeBtn.addEventListener('click', handleClose);
    rightSection.appendChild(closeBtn);

    top.appendChild(rightSection);

    header.appendChild(top);

    // Additional info in header
    const headerInfo = document.createElement('div');
    headerInfo.style.cssText = 'display: flex; gap: 1.5rem; align-items: center; flex-wrap: wrap; margin-top: 16px; padding-top: 16px; border-top: 1px solid rgba(56, 151, 152, 0.1);';

    // Source badge
    const source = error?.source || 'unknown';
    const sourceConfig = {
      frontend: { text: 'App', color: '#79AFBC', bg: 'rgba(37, 101, 125, 0.1)', border: 'rgba(37, 101, 125, 0.3)' },
      backend: { text: 'API', color: '#29768e', bg: 'rgba(56, 151, 152, 0.1)', border: 'rgba(56, 151, 152, 0.3)' },
      electron: { text: 'Electron', color: '#29768e', bg: 'rgba(41, 118, 142, 0.1)', border: 'rgba(41, 118, 142, 0.3)' },
    };
    const sourceStyle = sourceConfig[source] || { text: source.toUpperCase(), color: '#94a3b8', bg: 'rgba(80, 140, 148, 0.1)', border: 'rgba(80, 140, 148, 0.3)' };

    const sourceInfo = document.createElement('div');
    sourceInfo.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';
    sourceInfo.innerHTML = `
      <span class="ad-pill ad-pill--plain" style="--ad-tone-fg: ${sourceStyle.color}; --ad-tone-bg: ${sourceStyle.bg};">${sourceStyle.text}</span>
    `;
    headerInfo.appendChild(sourceInfo);

    // Component
    if (error?.component) {
      const componentInfo = document.createElement('div');
      componentInfo.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';
      componentInfo.innerHTML = `
        <span class="material-symbols-outlined" style="font-size: 18px; color: #508C94;">widgets</span>
        <span style="font-size: 0.875rem; color: var(--text-secondary);">${escapeHtml(error.component)}</span>
      `;
      headerInfo.appendChild(componentInfo);
    }

    // Created date
    const dateInfo = document.createElement('div');
    dateInfo.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';
    const createdAt = formatDateTime(error?.createdAt);
    dateInfo.innerHTML = `
      <span class="material-symbols-outlined" style="font-size: 18px; color: #508C94;">schedule</span>
      <span style="font-size: 0.875rem; color: var(--text-secondary);">${escapeHtml(createdAt)}</span>
    `;
    headerInfo.appendChild(dateInfo);

    // Occurrences
    const occurrenceInfo = document.createElement('div');
    occurrenceInfo.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';
    const occurrences = error?.occurrenceCount || 1;
    occurrenceInfo.innerHTML = `
      <span class="material-symbols-outlined" style="font-size: 18px; color: #508C94;">sync</span>
      <span style="font-size: 0.875rem; color: var(--text-secondary);">${occurrences} occurrence${occurrences > 1 ? 's' : ''}</span>
    `;
    headerInfo.appendChild(occurrenceInfo);

    header.appendChild(headerInfo);

    return header;
  }

  function createGeneralInfoSection() {
    const section = document.createElement('section');
    section.className = 'details-panel-section';

    const sectionHeader = document.createElement('div');
    sectionHeader.className = 'details-panel-section-header';
    sectionHeader.innerHTML = `
      <h4 class="details-panel-section-title">
        <span class="material-symbols-outlined" style="font-size: 18px; vertical-align: middle; margin-right: 0.5rem;">info</span>
        Informations générales
      </h4>
    `;
    section.appendChild(sectionHeader);

    const infoGrid = document.createElement('div');
    infoGrid.style.cssText = 'display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1rem;';

    // Déterminer la couleur de l'environnement
    const envColorMap = {
      'local': '#60a5fa',      // Bleu clair
      'staging': '#fbbf24',    // Orange clair
      'production': '#f87171', // Rouge clair
      'unknown': '#9ca3af',    // Gris
    };
    const envColor = envColorMap[error?.environment] || envColorMap['unknown'];
    const envLabel = error?.environment ? error.environment.toUpperCase() : '—';

    const infoItems = [
      { icon: 'tag', label: 'ID', value: formatValue(error?.id || error?._id), mono: true },
      { icon: 'cloud_circle', label: 'Environnement', value: envLabel, color: envColor },
      { icon: 'label', label: 'Niveau', value: formatValue(error?.level) },
      { icon: 'cloud', label: 'Source', value: formatValue(error?.source) },
      { icon: 'widgets', label: 'Composant', value: formatValue(error?.component) },
      { icon: 'article', label: 'Contexte', value: formatValue(error?.context) },
      { icon: error?.resolved ? 'check_circle' : 'cancel', label: 'Statut', value: error?.resolved ? 'Résolue' : 'Non résolue', color: error?.resolved ? '#2e9e6a' : '#c9453a' },
      { icon: 'schedule', label: 'Créé le', value: formatDateTime(error?.createdAt) },
      { icon: 'update', label: 'Modifié le', value: formatDateTime(error?.updatedAt || error?.createdAt) },
      { icon: 'history', label: 'Première occurrence', value: formatDateTime(error?.firstOccurrence || error?.createdAt) },
      { icon: 'history', label: 'Dernière occurrence', value: formatDateTime(error?.lastOccurrence || error?.createdAt) },
    ];

    infoItems.forEach(item => {
      const itemDiv = document.createElement('div');
      itemDiv.style.cssText = 'padding: 0.75rem; background: var(--bg-card); border: 1px solid var(--border-secondary); border-radius: 0.5rem;';
      itemDiv.innerHTML = `
        <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem;">
          <span class="material-symbols-outlined" style="font-size: 16px; color: #508C94;">${item.icon}</span>
          <span style="font-size: 0.75rem; color: #508C94;  font-weight: 600;">${item.label}</span>
        </div>
        <div style="font-size: 0.875rem; color: ${item.color || 'var(--text-primary)'}; font-family: ${item.mono ? "'JetBrains Mono', monospace" : 'inherit'}; word-break: break-word;">
          ${escapeHtml(item.value)}
        </div>
      `;
      infoGrid.appendChild(itemDiv);
    });

    // Tags
    if (error?.tags && error.tags.length > 0) {
      const tagsDiv = document.createElement('div');
      tagsDiv.style.cssText = 'padding: 0.75rem; background: var(--bg-card); border: 1px solid var(--border-secondary); border-radius: 0.5rem; grid-column: 1 / -1;';
      tagsDiv.innerHTML = `
        <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem;">
          <span class="material-symbols-outlined" style="font-size: 16px; color: #508C94;">local_offer</span>
          <span style="font-size: 0.75rem; color: #508C94;  font-weight: 600;">Tags</span>
        </div>
        <div style="display: flex; flex-wrap: wrap; gap: 0.5rem;">
          ${error.tags.map(tag => `
            <span style="font-size: 0.75rem; padding: 0.25rem 0.5rem; background: rgba(56, 151, 152, 0.1); color: #389798; border: 1px solid rgba(56, 151, 152, 0.3); border-radius: 0.25rem;">
              ${escapeHtml(tag)}
            </span>
          `).join('')}
        </div>
      `;
      infoGrid.appendChild(tagsDiv);
    }

    section.appendChild(infoGrid);
    return section;
  }

  function createMessageSection() {
    const section = document.createElement('section');
    section.className = 'details-panel-section';

    const sectionHeader = document.createElement('div');
    sectionHeader.className = 'details-panel-section-header';
    sectionHeader.innerHTML = `
      <h4 class="details-panel-section-title">
        <span class="material-symbols-outlined" style="font-size: 18px; vertical-align: middle; margin-right: 0.5rem;">message</span>
        Message
      </h4>
    `;
    section.appendChild(sectionHeader);

    const messageContainer = document.createElement('div');
    messageContainer.style.cssText = 'padding: 1.25rem; background: var(--bg-secondary); border: 1px solid var(--border-primary); border-radius: 0.5rem;';

    // Message
    if (error?.message) {
      messageContainer.innerHTML = `
        <div style="margin-bottom: ${error?.errorMessage ? '1.5rem' : '0'};">
          <div style="font-size: 0.75rem; color: #508C94;  margin-bottom: 0.5rem; font-weight: 600;">Message</div>
          <pre style="margin: 0; max-height: 300px; overflow-y: auto; white-space: pre-wrap; word-wrap: break-word; font-family: 'JetBrains Mono', monospace; font-size: 0.875rem; color: var(--text-primary); line-height: 1.6;">${escapeHtml(formatValue(error.message))}</pre>
        </div>
      `;
    }

    // Error message
    if (error?.errorMessage) {
      const errorMessageDiv = document.createElement('div');
      errorMessageDiv.innerHTML = `
        <div style="font-size: 0.75rem; color: #508C94;  margin-bottom: 0.5rem; font-weight: 600;">Message d'erreur</div>
        <pre style="margin: 0; max-height: 300px; overflow-y: auto; white-space: pre-wrap; word-wrap: break-word; font-family: 'JetBrains Mono', monospace; font-size: 0.875rem; color: #c9453a; line-height: 1.6;">${escapeHtml(formatValue(error.errorMessage))}</pre>
      `;
      messageContainer.appendChild(errorMessageDiv);
    }

    section.appendChild(messageContainer);
    return section;
  }

  function createStackSection() {
    const section = document.createElement('section');
    section.className = 'details-panel-section';

    const sectionHeader = document.createElement('div');
    sectionHeader.className = 'details-panel-section-header';
    sectionHeader.innerHTML = `
      <h4 class="details-panel-section-title">
        <span class="material-symbols-outlined" style="font-size: 18px; vertical-align: middle; margin-right: 0.5rem;">view_list</span>
        Stack Trace
      </h4>
    `;
    section.appendChild(sectionHeader);

    const stackContainer = document.createElement('div');
    stackContainer.style.cssText = 'padding: 1.25rem; background: var(--bg-secondary); border: 1px solid var(--border-primary); border-radius: 0.5rem;';
    stackContainer.innerHTML = `
      <pre style="margin: 0; max-height: 400px; overflow-y: auto; white-space: pre-wrap; word-wrap: break-word; font-family: 'JetBrains Mono', monospace; font-size: 0.8125rem; color: var(--text-secondary); line-height: 1.6;">${escapeHtml(formatValue(error.stack))}</pre>
    `;

    section.appendChild(stackContainer);
    return section;
  }

  function createUserSection() {
    const section = document.createElement('section');
    section.className = 'details-panel-section';

    const sectionHeader = document.createElement('div');
    sectionHeader.className = 'details-panel-section-header';
    sectionHeader.innerHTML = `
      <h4 class="details-panel-section-title">
        <span class="material-symbols-outlined" style="font-size: 18px; vertical-align: middle; margin-right: 0.5rem;">person</span>
        Utilisateur
      </h4>
    `;
    section.appendChild(sectionHeader);

    const userGrid = document.createElement('div');
    userGrid.style.cssText = 'display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem;';

    const userItems = [
      { icon: 'tag', label: 'ID Utilisateur', value: formatValue(error?.userId), mono: true },
      { icon: 'email', label: 'Email', value: formatValue(error?.userEmail) },
      { icon: 'badge', label: 'Nom', value: formatValue(error?.userName) },
      { icon: 'admin_panel_settings', label: 'Rôle', value: formatValue(error?.userRole) },
    ];

    userItems.forEach(item => {
      const itemDiv = document.createElement('div');
      itemDiv.style.cssText = 'padding: 0.75rem; background: var(--bg-card); border: 1px solid var(--border-secondary); border-radius: 0.5rem;';
      itemDiv.innerHTML = `
        <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem;">
          <span class="material-symbols-outlined" style="font-size: 16px; color: #508C94;">${item.icon}</span>
          <span style="font-size: 0.75rem; color: #508C94;  font-weight: 600;">${item.label}</span>
        </div>
        <div style="font-size: 0.875rem; color: var(--text-primary); font-family: ${item.mono ? "'JetBrains Mono', monospace" : 'inherit'}; word-break: break-word;">
          ${escapeHtml(item.value)}
        </div>
      `;
      userGrid.appendChild(itemDiv);
    });

    section.appendChild(userGrid);
    return section;
  }

  function createRequestSection() {
    const section = document.createElement('section');
    section.className = 'details-panel-section';

    const sectionHeader = document.createElement('div');
    sectionHeader.className = 'details-panel-section-header';
    sectionHeader.innerHTML = `
      <h4 class="details-panel-section-title">
        <span class="material-symbols-outlined" style="font-size: 18px; vertical-align: middle; margin-right: 0.5rem;">http</span>
        Contexte de la requête
      </h4>
    `;
    section.appendChild(sectionHeader);

    const requestGrid = document.createElement('div');
    requestGrid.style.cssText = 'display: grid; grid-template-columns: 1fr; gap: 1rem;';

    const requestItems = [
      { icon: 'link', label: 'URL', value: formatValue(error?.url), mono: true },
      { icon: 'route', label: 'Path', value: formatValue(error?.path), mono: true },
      { icon: 'label', label: 'Méthode', value: formatValue(error?.method) },
      { icon: 'numbers', label: 'Status Code', value: formatValue(error?.statusCode) },
      { icon: 'tag', label: 'Request ID', value: formatValue(error?.requestId), mono: true },
      { icon: 'public', label: 'Adresse IP', value: formatValue(error?.ip), mono: true },
      { icon: 'devices', label: 'User Agent', value: formatValue(error?.userAgent), mono: true },
      { icon: 'arrow_back', label: 'Référent', value: formatValue(error?.referrer), mono: true },
    ];

    requestItems.forEach(item => {
      if (item.value !== '—') {
        const itemDiv = document.createElement('div');
        itemDiv.style.cssText = 'padding: 0.75rem; background: var(--bg-card); border: 1px solid var(--border-secondary); border-radius: 0.5rem;';
        itemDiv.innerHTML = `
          <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem;">
            <span class="material-symbols-outlined" style="font-size: 16px; color: #508C94;">${item.icon}</span>
            <span style="font-size: 0.75rem; color: #508C94;  font-weight: 600;">${item.label}</span>
          </div>
          <div style="font-size: 0.8125rem; color: var(--text-primary); font-family: ${item.mono ? "'JetBrains Mono', monospace" : 'inherit'}; word-break: break-all; white-space: pre-wrap;">
            ${escapeHtml(item.value)}
          </div>
        `;
        requestGrid.appendChild(itemDiv);
      }
    });

    section.appendChild(requestGrid);
    return section;
  }

  function createCodeSection() {
    const section = document.createElement('section');
    section.className = 'details-panel-section';

    const sectionHeader = document.createElement('div');
    sectionHeader.className = 'details-panel-section-header';
    sectionHeader.innerHTML = `
      <h4 class="details-panel-section-title">
        <span class="material-symbols-outlined" style="font-size: 18px; vertical-align: middle; margin-right: 0.5rem;">code</span>
        Localisation du code
      </h4>
    `;
    section.appendChild(sectionHeader);

    const codeGrid = document.createElement('div');
    codeGrid.style.cssText = 'display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem;';

    const codeItems = [
      { icon: 'description', label: 'Fichier', value: formatValue(error?.file), mono: true, fullWidth: true },
      { icon: 'format_list_numbered', label: 'Ligne', value: formatValue(error?.line) },
      { icon: 'view_column', label: 'Colonne', value: formatValue(error?.column) },
      { icon: 'function', label: 'Fonction', value: formatValue(error?.function), mono: true },
    ];

    codeItems.forEach(item => {
      if (item.value !== '—') {
        const itemDiv = document.createElement('div');
        itemDiv.style.cssText = `padding: 0.75rem; background: var(--bg-card); border: 1px solid var(--border-secondary); border-radius: 0.5rem; ${item.fullWidth ? 'grid-column: 1 / -1;' : ''}`;
        itemDiv.innerHTML = `
          <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem;">
            <span class="material-symbols-outlined" style="font-size: 16px; color: #508C94;">${item.icon}</span>
            <span style="font-size: 0.75rem; color: #508C94;  font-weight: 600;">${item.label}</span>
          </div>
          <div style="font-size: 0.875rem; color: var(--text-primary); font-family: ${item.mono ? "'JetBrains Mono', monospace" : 'inherit'}; word-break: break-all;">
            ${escapeHtml(item.value)}
          </div>
        `;
        codeGrid.appendChild(itemDiv);
      }
    });

    section.appendChild(codeGrid);
    return section;
  }

  function createEnvironmentSection() {
    const section = document.createElement('section');
    section.className = 'details-panel-section';

    const sectionHeader = document.createElement('div');
    sectionHeader.className = 'details-panel-section-header';
    sectionHeader.innerHTML = `
      <h4 class="details-panel-section-title">
        <span class="material-symbols-outlined" style="font-size: 18px; vertical-align: middle; margin-right: 0.5rem;">computer</span>
        Environnement
      </h4>
    `;
    section.appendChild(sectionHeader);

    const envGrid = document.createElement('div');
    envGrid.style.cssText = 'display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem;';

    // Déterminer la couleur de l'environnement
    const envColorMap = {
      'local': '#389798',      // Bleu
      'staging': '#f59e0b',    // Orange
      'production': '#ef4444', // Rouge
      'unknown': '#6b7280',    // Gris
    };
    const envColor = envColorMap[error?.environment] || envColorMap['unknown'];

    const envItems = [
      { icon: 'cloud', label: 'Environnement API', value: formatValue(error?.environment), color: envColor, bold: true },
      { icon: 'link', label: 'URL API', value: formatValue(error?.apiUrl), mono: true, fullWidth: true },
      { icon: 'settings_suggest', label: 'Mode configuration', value: formatValue(error?.environmentConfig) },
      { icon: 'web', label: 'Navigateur', value: formatValue(error?.browserInfo?.name) },
      { icon: 'tag', label: 'Version navigateur', value: formatValue(error?.browserInfo?.version) },
      { icon: 'devices', label: 'Plateforme', value: formatValue(error?.browserInfo?.platform) },
      { icon: 'language', label: 'Langue', value: formatValue(error?.browserInfo?.language) },
      { icon: 'monitor', label: 'Résolution écran', value: formatValue(error?.browserInfo?.screenResolution) },
      { icon: 'aspect_ratio', label: 'Taille viewport', value: formatValue(error?.browserInfo?.viewportSize) },
      { icon: 'developer_mode', label: 'Version Electron', value: formatValue(error?.electronVersion) },
      { icon: 'settings', label: 'Version OS', value: formatValue(error?.osVersion) },
    ];

    envItems.forEach(item => {
      if (item.value !== '—') {
        const itemDiv = document.createElement('div');
        const gridColumnStyle = item.fullWidth ? 'grid-column: 1 / -1;' : '';
        itemDiv.style.cssText = `padding: 0.75rem; background: var(--bg-card); border: 1px solid var(--border-secondary); border-radius: 0.5rem; ${gridColumnStyle}`;
        
        const valueColor = item.color || 'var(--text-primary)';
        const fontWeight = item.bold ? '700' : '400';
        const fontFamily = item.mono ? "'JetBrains Mono', monospace" : 'inherit';
        
        itemDiv.innerHTML = `
          <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem;">
            <span class="material-symbols-outlined" style="font-size: 16px; color: #508C94;">${item.icon}</span>
            <span style="font-size: 0.75rem; color: #508C94;  font-weight: 600;">${item.label}</span>
          </div>
          <div style="font-size: 0.875rem; color: ${valueColor}; font-weight: ${fontWeight}; font-family: ${fontFamily}; word-break: break-all;">
            ${escapeHtml(item.value)}
          </div>
        `;
        envGrid.appendChild(itemDiv);
      }
    });

    // Browser info complet si disponible
    if (error?.browserInfo && typeof error.browserInfo === 'object') {
      const browserInfoDiv = document.createElement('div');
      browserInfoDiv.style.cssText = 'padding: 0.75rem; background: var(--bg-card); border: 1px solid var(--border-secondary); border-radius: 0.5rem; grid-column: 1 / -1;';
      browserInfoDiv.innerHTML = `
        <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem;">
          <span class="material-symbols-outlined" style="font-size: 16px; color: #508C94;">data_object</span>
          <span style="font-size: 0.75rem; color: #508C94;  font-weight: 600;">Browser Info (complet)</span>
        </div>
        <pre style="margin: 0; max-height: 200px; overflow-y: auto; white-space: pre-wrap; word-wrap: break-word; font-family: 'JetBrains Mono', monospace; font-size: 0.8125rem; color: var(--text-secondary); line-height: 1.6; background: var(--bg-secondary); padding: 0.75rem; border-radius: 0.25rem;">${escapeHtml(JSON.stringify(error.browserInfo, null, 2))}</pre>
      `;
      envGrid.appendChild(browserInfoDiv);
    }

    section.appendChild(envGrid);
    return section;
  }

  function createMetadataSection() {
    const section = document.createElement('section');
    section.className = 'details-panel-section';

    const sectionHeader = document.createElement('div');
    sectionHeader.className = 'details-panel-section-header';
    sectionHeader.innerHTML = `
      <h4 class="details-panel-section-title">
        <span class="material-symbols-outlined" style="font-size: 18px; vertical-align: middle; margin-right: 0.5rem;">data_object</span>
        Métadonnées
      </h4>
    `;
    section.appendChild(sectionHeader);

    const metadataContainer = document.createElement('div');
    metadataContainer.style.cssText = 'padding: 1.25rem; background: var(--bg-secondary); border: 1px solid var(--border-primary); border-radius: 0.5rem;';
    metadataContainer.innerHTML = `
      <pre style="margin: 0; max-height: 400px; overflow-y: auto; white-space: pre-wrap; word-wrap: break-word; font-family: 'JetBrains Mono', monospace; font-size: 0.8125rem; color: var(--text-secondary); line-height: 1.6;">${escapeHtml(JSON.stringify(error.metadata, null, 2))}</pre>
    `;

    section.appendChild(metadataContainer);
    return section;
  }

  function createResolutionSection() {
    const section = document.createElement('section');
    section.className = 'details-panel-section';

    const sectionHeader = document.createElement('div');
    sectionHeader.className = 'details-panel-section-header';
    sectionHeader.innerHTML = `
      <h4 class="details-panel-section-title">
        <span class="material-symbols-outlined" style="font-size: 18px; vertical-align: middle; margin-right: 0.5rem;">${error?.resolved ? 'check_circle' : 'pending'}</span>
        Résolution
      </h4>
    `;
    section.appendChild(sectionHeader);

    const resolutionGrid = document.createElement('div');
    resolutionGrid.style.cssText = 'display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem;';

    const resolutionItems = [
      { icon: error?.resolved ? 'check_circle' : 'cancel', label: 'Résolu', value: error?.resolved ? 'Oui' : 'Non', color: error?.resolved ? '#2e9e6a' : '#c9453a' },
    ];

    if (error?.resolved) {
      resolutionItems.push(
        { icon: 'person', label: 'Résolu par', value: formatValue(error?.resolvedByName) },
        { icon: 'schedule', label: 'Résolu le', value: formatDateTime(error?.resolvedAt) }
      );
    }

    resolutionItems.forEach(item => {
      const itemDiv = document.createElement('div');
      itemDiv.style.cssText = 'padding: 0.75rem; background: var(--bg-card); border: 1px solid var(--border-secondary); border-radius: 0.5rem;';
      itemDiv.innerHTML = `
        <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem;">
          <span class="material-symbols-outlined" style="font-size: 16px; color: #508C94;">${item.icon}</span>
          <span style="font-size: 0.75rem; color: #508C94;  font-weight: 600;">${item.label}</span>
        </div>
        <div style="font-size: 0.875rem; color: ${item.color || 'var(--text-primary)'};">
          ${escapeHtml(item.value)}
        </div>
      `;
      resolutionGrid.appendChild(itemDiv);
    });

    // Resolution note if available
    if (error?.resolved && error?.resolutionNote) {
      const noteDiv = document.createElement('div');
      noteDiv.style.cssText = 'padding: 0.75rem; background: var(--bg-card); border: 1px solid var(--border-secondary); border-radius: 0.5rem; grid-column: 1 / -1;';
      noteDiv.innerHTML = `
        <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem;">
          <span class="material-symbols-outlined" style="font-size: 16px; color: #508C94;">note</span>
          <span style="font-size: 0.75rem; color: #508C94;  font-weight: 600;">Note de résolution</span>
        </div>
        <pre style="margin: 0; max-height: 150px; overflow-y: auto; white-space: pre-wrap; word-wrap: break-word; font-family: 'JetBrains Mono', monospace; font-size: 0.875rem; color: var(--text-primary); line-height: 1.6; background: var(--bg-secondary); padding: 0.75rem; border-radius: 0.25rem;">${escapeHtml(formatValue(error.resolutionNote))}</pre>
      `;
      resolutionGrid.appendChild(noteDiv);
    }

    section.appendChild(resolutionGrid);
    return section;
  }

  function createFooter() {
    const footer = document.createElement('footer');
    footer.className = 'details-panel-footer';

    const actions = document.createElement('div');
    actions.className = 'details-panel-actions';

    const formContainer = document.createElement('div');
    formContainer.style.cssText = 'width: 100%; display: flex; flex-direction: column; gap: 1rem;';

    const textarea = document.createElement('textarea');
    textarea.id = 'error-resolution-note';
    textarea.placeholder = 'Note de résolution (optionnel)...';
    textarea.rows = 3;
    textarea.style.cssText = 'width: 100%; padding: 0.75rem; background: var(--bg-input); border: 2px solid var(--border-primary); border-radius: 0.5rem; color: var(--text-primary); font-family: inherit; font-size: 0.875rem; line-height: 1.6; resize: vertical;';
    formContainer.appendChild(textarea);

    const resolveBtn = document.createElement('button');
    resolveBtn.className = 'action-main-btn';
    resolveBtn.classList.add('rd-btn', 'rd-btn-night');
    resolveBtn.innerHTML = `
      <span class="material-symbols-outlined" style="font-size: 18px;">check_circle</span>
      <span>Marquer comme résolu</span>
    `;

    resolveBtn.addEventListener('click', async () => {
      const note = textarea.value.trim();
      await handleResolveError(note);
    });

    formContainer.appendChild(resolveBtn);
    actions.appendChild(formContainer);
    footer.appendChild(actions);

    return footer;
  }

  async function handleResolveError(resolutionNote = '') {
    const errorId = error?.id || error?._id;
    if (!errorId) return;

    try {
      const response = await apiFetch(`/api/errors/${errorId}/resolve`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolutionNote }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erreur lors de la résolution');
      }

      // Update error status
      error.resolved = true;
      error.resolutionNote = resolutionNote;

      // Show success message
      if (typeof window.showSuccessModal === 'function') {
        window.showSuccessModal('L\'erreur a été marquée comme résolue avec succès.', 'Erreur résolue', () => {
          if (onErrorUpdated && typeof onErrorUpdated === 'function') {
            onErrorUpdated();
          }
          handleClose();
        });
      } else {
        if (onErrorUpdated && typeof onErrorUpdated === 'function') {
          onErrorUpdated();
        }
        handleClose();
      }
    } catch (err) {
      console.error('Error resolving error', err);
      if (typeof window.showGlobalNotification === 'function') {
        window.showGlobalNotification('error', err.message || 'Erreur lors de la résolution de l\'erreur');
      } else {
        alert(err.message || 'Erreur lors de la résolution de l\'erreur');
      }
    }
  }

  function handleClose() {
    overlay.classList.remove('active');
    panel.classList.remove('active');
    setTimeout(() => {
      container.remove();
      if (onClose) {
        onClose();
      }
    }, 300);
  }

  // Close on Escape key
  const escapeHandler = (e) => {
    if (e.key === 'Escape') {
      handleClose();
      document.removeEventListener('keydown', escapeHandler);
    }
  };
  document.addEventListener('keydown', escapeHandler);

  return container;
}
